import React, { useEffect, useState, useCallback } from "react";
import { apiClient } from "@/lib/apiClient";
import { useCompany } from "@/context/CompanyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Store, Copy, RefreshCw, ExternalLink, ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Woocommerce() {
  const { company } = useCompany();
  const [settings, setSettings] = useState(null);
  const [sourceUrl, setSourceUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [taxRate, setTaxRate] = useState(8.1);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rotated, setRotated] = useState(null);

  const load = useCallback(async () => {
    if (!company?._id) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/companies/${company._id}/woocommerce-settings`);
      setSettings(res.data);
      setSourceUrl(res.data.source_url || "");
      setEnabled(!!res.data.enabled);
      setTaxRate(res.data.default_tax_rate ?? 8.1);
      setSecret("");
    } catch (e) {
      toast.error("Konnte WooCommerce-Einstellungen nicht laden.");
    } finally {
      setLoading(false);
    }
  }, [company?._id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (!company?._id) return;
    setSaving(true);
    try {
      const body = {
        enabled,
        source_url: sourceUrl.trim() || null,
        default_tax_rate: parseFloat(taxRate),
      };
      if (secret) body.webhook_secret = secret;
      const res = await apiClient.put(`/companies/${company._id}/woocommerce-settings`, body);
      setSettings(res.data);
      setSecret("");
      toast.success("WooCommerce-Einstellungen gespeichert.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const handleRotate = async () => {
    if (!company?._id) return;
    if (!window.confirm(
      "Neuen Webhook-Secret generieren? Du musst ihn sofort in WooCommerce aktualisieren — sonst werden alle Webhooks abgelehnt."
    )) return;
    try {
      const res = await apiClient.post(`/companies/${company._id}/woocommerce-settings/rotate-secret`);
      setRotated(res.data.webhook_secret);
      toast.success("Neuer Webhook-Secret generiert.");
      load();
    } catch (e) {
      toast.error("Rotate fehlgeschlagen.");
    }
  };

  const copyToClipboard = (text, label = "Kopiert") => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  const webhookUrl = settings?.webhook_url
    ? `${BACKEND_URL}${settings.webhook_url}`
    : `${BACKEND_URL}/api/webhooks/woocommerce`;

  if (loading) {
    return (
      <div className="p-6 text-slate-500 text-sm" data-testid="woocommerce-loading">
        Lade…
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="woocommerce-page">
      <div className="flex items-center gap-3">
        <Link
          to="/settings"
          className="text-sm text-slate-500 hover:text-slate-900"
          data-testid="woocommerce-back-link"
        >
          ← Einstellungen
        </Link>
      </div>
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-white shadow-sm">
          <Store className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">WooCommerce-Connector</h1>
          <p className="text-sm text-slate-500 mt-1">
            Empfange WooCommerce-Bestellungen automatisch als Rechnungen.
            HMAC-SHA256-verifiziert, Multi-Shop-fähig per Source-URL, Replay-geschützt.
          </p>
        </div>
        <Badge
          className={
            settings?.enabled
              ? "bg-purple-100 text-purple-800 border-purple-200"
              : "bg-slate-100 text-slate-600 border-slate-200"
          }
          data-testid="woocommerce-status-badge"
        >
          {settings?.enabled ? "AKTIV" : "INAKTIV"}
        </Badge>
      </div>

      <Card className="p-6 space-y-5 border border-slate-200 bg-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-slate-900">Connector aktivieren</div>
            <p className="text-xs text-slate-500 mt-0.5">
              Wenn aktiv und Source-URL + Secret gesetzt sind, akzeptiert der Endpunkt
              eingehende Webhooks.
            </p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid="woocommerce-enabled-switch"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Shop-URL (Source)</label>
          <Input
            type="text"
            placeholder="https://mein-shop.ch"
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            data-testid="woocommerce-source-url-input"
          />
          <p className="text-xs text-slate-500">
            Volle URL deines WooCommerce-Shops. Routing erfolgt anhand des Headers{" "}
            <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">X-WC-Webhook-Source</code>.
            URL wird normalisiert (case-insensitive, ohne trailing slash).
          </p>
          {settings?.source_normalized && (
            <p className="text-xs text-slate-400 font-mono">
              Normalisiert: {settings.source_normalized}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
            Webhook-Secret
            {settings?.has_secret && (
              <Badge variant="outline" className="text-[11px]">
                Aktuell: {settings.secret_preview}
              </Badge>
            )}
          </label>
          <Input
            type="password"
            placeholder={settings?.has_secret ? "(leer lassen, um aktuellen zu behalten)" : 'WooCommerce → Settings → Advanced → Webhooks → "Secret"'}
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            data-testid="woocommerce-secret-input"
          />
          <div className="flex items-center justify-between text-xs">
            <p className="text-slate-500">
              Manuell aus WooCommerce übernehmen <strong>ODER</strong> hier rotieren lassen:
            </p>
            <button
              type="button"
              onClick={handleRotate}
              className="flex items-center gap-1 text-purple-600 hover:text-purple-700 font-medium"
              data-testid="woocommerce-rotate-secret-btn"
            >
              <RefreshCw className="w-3 h-3" />
              Neuen Secret generieren
            </button>
          </div>
        </div>

        <div className="space-y-2 max-w-xs">
          <label className="text-sm font-medium text-slate-700">
            Default-MWST (%)
          </label>
          <Input
            type="number"
            step="0.1"
            min="0"
            max="100"
            value={taxRate}
            onChange={(e) => setTaxRate(e.target.value)}
            data-testid="woocommerce-tax-rate-input"
          />
          <p className="text-xs text-slate-500">
            Wird genutzt wenn die Order kein <code className="font-mono">total_tax</code> liefert.
          </p>
        </div>

        <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <Button onClick={load} variant="outline" disabled={saving} data-testid="woocommerce-reset-btn">
            Verwerfen
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="woocommerce-save-btn">
            {saving ? "Speichere…" : "Speichern"}
          </Button>
        </div>
      </Card>

      {rotated && (
        <Card className="p-4 border-2 border-amber-300 bg-amber-50" data-testid="woocommerce-rotated-banner">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-amber-900 text-sm">
                Neuer Webhook-Secret — wird nur jetzt angezeigt!
              </div>
              <div className="mt-2 flex items-center gap-2">
                <code className="block flex-1 min-w-0 p-2 bg-white rounded font-mono text-xs break-all border border-amber-200">
                  {rotated}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(rotated, "Secret kopiert")}
                  data-testid="woocommerce-copy-rotated-btn"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <p className="text-xs text-amber-700 mt-2">
                Trage diesen Wert jetzt in WooCommerce → Settings → Advanced →
                Webhooks → Secret ein.
              </p>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-6 border border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-4 h-4 text-purple-600" />
          <h2 className="font-semibold text-slate-900">Setup in WooCommerce</h2>
        </div>
        <ol className="space-y-4 text-sm text-slate-700">
          <li>
            <span className="font-medium">1. Webhook anlegen:</span>
            <p className="mt-1 text-slate-600">
              WooCommerce-Admin → <em>Settings</em> → <em>Advanced</em> → <em>Webhooks</em> → "Add webhook".
            </p>
          </li>
          <li>
            <span className="font-medium">2. Delivery URL und Status:</span>
            <div className="mt-2 flex items-center gap-2">
              <code className="block flex-1 p-2 bg-white rounded font-mono text-xs break-all border border-slate-200">
                {webhookUrl}
              </code>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(webhookUrl, "URL kopiert")}
                data-testid="woocommerce-copy-url-btn"
              >
                <Copy className="w-3 h-3" />
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Status: <code>Active</code> · API-Version: <code>WP REST API Integration v3</code>.
            </p>
          </li>
          <li>
            <span className="font-medium">3. Topics — pro Topic einen separaten Webhook anlegen:</span>
            <ul className="mt-1.5 ml-4 space-y-0.5 text-slate-600">
              <li>• <code className="font-mono text-xs bg-white px-1 rounded">Order created</code> → neue Bestellung → Draft-Rechnung</li>
              <li>• <code className="font-mono text-xs bg-white px-1 rounded">Order updated</code> → status=completed → Rechnung als bezahlt</li>
              <li>• <code className="font-mono text-xs bg-white px-1 rounded">Customer created</code> → Kunde upserten</li>
              <li>• <code className="font-mono text-xs bg-white px-1 rounded">Customer updated</code> → Kunde aktualisieren</li>
            </ul>
          </li>
          <li>
            <span className="font-medium">4. Secret:</span>
            <p className="mt-1 text-slate-600">
              Übernimm das Secret aus WooCommerce in das Feld oben — <strong>oder</strong> rotiere
              hier einen neuen Secret und trage ihn in WooCommerce ein. Bei jedem Webhook
              wird derselbe Secret genutzt.
            </p>
          </li>
          <li>
            <span className="font-medium">5. Testen:</span>
            <p className="mt-1 text-slate-600">
              Lege im Shop eine Test-Order an. Schau danach unter{" "}
              <Link to="/invoices" className="text-purple-600 hover:underline" data-testid="woocommerce-link-invoices">
                Rechnungen
              </Link>{" "}
              — die Order erscheint mit Quelle "woocommerce" und Nummer <code className="font-mono">WOO-YYYY-XXXXX</code>.
            </p>
          </li>
        </ol>
        <a
          href="https://woocommerce.com/document/webhooks/"
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex items-center gap-1 text-xs text-purple-600 hover:underline"
          data-testid="woocommerce-docs-link"
        >
          WooCommerce-Webhook-Docs öffnen <ExternalLink className="w-3 h-3" />
        </a>
      </Card>
    </div>
  );
}
